import PanelEntry from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/PanelEntry';
import React, { useEffect, useState } from 'react';
import {
  useFetchPayTags,
  fetchTags,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { Button, Icon, SelectMenu, Typography } from '@scale/scaleui';
import { Autocomplete as MUIAutocomplete, TextField as MuiTextField } from '@mui/material';
import Autocomplete from '../../components/SimpleAutocomplete';
import { useOCPStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useOCPStore';
import { useDraftFlowStore } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/hooks/useDraftFlowStore';
import {
  OnboardingFlowTransition,
  OCPWorkerSkillGrant,
  OnboardingFlowNodeType,
  OnboardingStepAttributeKey,
  getOnboardingStepAttributeMap,
} from '@scale/shared/onboardingControlPanel';
import { useFetchAllWorkerSkills } from 'react-scale/hooks/workerSkills/queries/useFetchAllWorkerSkills';
import {
  WorkerSkillDimensionQualityCategory,
  defaultWorkerSkillDimension,
} from '@scale/shared-types';
import { isSecureTag } from '@scale/shared/trustAndSafety/helpers';

interface WorkerSkillOption {
  id: string;
  title: string;
}

const GrantsConfigurator: React.FC = () => {
  const { currentFlow, detailTarget, canFreeEdit } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget as OnboardingFlowTransition,
    canFreeEdit: state.canFreeEdit,
  }));

  const { editDraftEntityValue } = useDraftFlowStore(state => ({
    editDraftEntityValue: state.editDraftEntityValue,
    draftFlows: state.draftFlows,
  }));

  const [showTagsConfigurator, setShowTagsConfigurator] = useState(false);
  const [tags, setTags] = useState<string[]>(detailTarget.tags || []);
  const [workerSkills, setWorkerSkills] = useState(detailTarget.workerSkills || []);
  const [allWorkerSkillOptions, setAllWorkerSkillOptions] = useState<WorkerSkillOption[]>([]);
  const [filteredWorkerSkillOptions, setFilteredWorkerSkillOptions] = useState<WorkerSkillOption[]>(
    [],
  );

  const [showAddTagsButton, setShowAddTagsButton] = useState(true);

  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [workerSkillFilterValue, setWorkerSkillFilterValue] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);

  const editableNodeMap = getOnboardingStepAttributeMap(OnboardingStepAttributeKey.Editable);

  useEffect(() => {
    if (currentFlow && detailTarget) {
      const destinationNode = currentFlow.nodes.find(node => node.name === detailTarget.destNode);
      if (destinationNode && destinationNode.type === OnboardingFlowNodeType.Step) {
        setDisabled(
          !(canFreeEdit && currentFlow?.freeEdit) &&
            !editableNodeMap[destinationNode.stepType] &&
            destinationNode.name !== 'Skill Selection',
        );
      }
      setWorkerSkills(detailTarget.workerSkills || []);
      setTags(detailTarget.tags || []);
    }
  }, [currentFlow, detailTarget]);

  const handleAddWorkerSkill = () => {
    const newSkill: OCPWorkerSkillGrant = {
      id: '',
      name: '',
      dimension: defaultWorkerSkillDimension,
      category: WorkerSkillDimensionQualityCategory.Unranked,
    };
    const updatedWorkerSkills = [...workerSkills, newSkill];
    setWorkerSkills(updatedWorkerSkills);
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget, workerSkills: updatedWorkerSkills };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleUpdateWorkerSkill = (index: number, field: string, value: any) => {
    const updatedSkills = [...workerSkills];
    if (field === 'workerSkill') {
      updatedSkills[index] = { ...updatedSkills[index], id: value.id, name: value.title };
    } else {
      updatedSkills[index] = { ...updatedSkills[index], [field]: value };
    }
    setWorkerSkills(updatedSkills);
    // Update the draft entity as well if needed
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget, workerSkills: updatedSkills };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const deleteWorkerSkill = (index: number) => {
    const updatedSkills = workerSkills.filter((_skill, idx) => idx !== index);
    setWorkerSkills(updatedSkills);
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget, workerSkills: updatedSkills };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleEditTags = (newValue: string[]) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.tags = newValue;
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
      setTags(newValue);
    }
  };

  const { data: payTags, isLoading: loadingPayTags } = useFetchPayTags();

  const handleFetchTags = async (search?: string) => {
    setLoadingTags(true);
    if (search && payTags) {
      const tags = await fetchTags(search);
      // filter out payTags and secure tags
      const filteredTags = tags.filter(tag => {
        return !payTags.includes(tag) && !isSecureTag(tag);
      });
      setTagOptions(filteredTags);
    }
    setLoadingTags(false);
  };

  const {
    data: allSkills,
    isLoading: loadingSkills,
    refetch: refetchSkills,
  } = useFetchAllWorkerSkills();

  useEffect(() => {
    if (!allSkills) {
      refetchSkills();
    }
  }, [allSkills, refetchSkills]);

  useEffect(() => {
    if (!loadingSkills && allSkills) {
      const transformedSkills = allSkills.map(skill => ({
        id: skill.id,
        title: skill.name,
      }));
      setAllWorkerSkillOptions(transformedSkills);
    }
  }, [loadingSkills, allSkills]);

  useEffect(() => {
    if (workerSkillFilterValue) {
      const filteredWS = allWorkerSkillOptions.filter(skill =>
        skill.title.toLowerCase().includes(workerSkillFilterValue.toLowerCase()),
      );
      setFilteredWorkerSkillOptions(filteredWS);
    }
  }, [workerSkillFilterValue]);

  const handleShowAddTagsClick = () => {
    setShowTagsConfigurator(true);
    setShowAddTagsButton(false);
  };

  return (
    <div>
      <PanelEntry title={'Grants'}>
        <Typography.Paragraph3>
          Contributors will be granted these tags or worker skills when they take this transition.
          Pay tags and secure tags are not included.
        </Typography.Paragraph3>

        {showTagsConfigurator && (
          <>
            <Typography.Paragraph3>Tags</Typography.Paragraph3>

            <MUIAutocomplete
              multiple
              disablePortal
              onChange={(_event, newValue) => {
                handleEditTags(newValue);
              }}
              value={tags}
              id="grant-tags-input"
              options={tagOptions}
              loading={loadingTags || loadingPayTags}
              sx={{ width: 500 }}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              onKeyDown={() => {
                handleFetchTags(inputValue);
              }}
              renderInput={params => <MuiTextField {...params} />}
            />
          </>
        )}

        {showAddTagsButton && (
          <div>
            <div className="grid grid-cols-3">
              {tags && tags.length > 0 ? (
                tags.map((tag, index) => (
                  <div key={index} className="text-center break-words min-w-0">
                    <Typography.Paragraph3 className="bg-gray-300 rounded p-2 text-xs">
                      {tag}
                    </Typography.Paragraph3>
                  </div>
                ))
              ) : (
                <Typography.Paragraph3 className="col-span-3">
                  No Tags on this transition
                </Typography.Paragraph3>
              )}
            </div>
            <Button
              variant="transparent"
              className="w-fit ml-2"
              onClick={handleShowAddTagsClick}
              disabled={disabled}
            >
              + Add Tags
            </Button>
          </div>
        )}
      </PanelEntry>

      <PanelEntry title={'Worker Skills'}>
        <Typography.Paragraph3>
          Contributors will be granted these worker skills when they take this transition.
        </Typography.Paragraph3>
        <div className="flex items-center gap-4 mt-1 mb-1">
          <Typography.Paragraph3 className="flex-1 text-center">Skill Name</Typography.Paragraph3>
          <Typography.Paragraph3 className="flex-1 text-center">Category</Typography.Paragraph3>
          <Typography.Paragraph3 className="w-8"> </Typography.Paragraph3>
        </div>
        {workerSkills.map((skill, index) => (
          <div key={index} className="flex items-center gap-4 my-2">
            <Autocomplete
              className="flex-[2]"
              options={filteredWorkerSkillOptions}
              value={{ id: skill.id, title: skill.name }}
              setSelectedOption={option => {
                handleUpdateWorkerSkill(index, 'workerSkill', option);
              }}
              onInputChange={setWorkerSkillFilterValue}
              disabled={disabled}
              placeholder="Search Worker Skills by name..."
            />
            <SelectMenu
              className="flex-1"
              options={Object.values(WorkerSkillDimensionQualityCategory).map(category => ({
                id: category,
                name: category,
              }))}
              setSelected={e => handleUpdateWorkerSkill(index, 'category', e)}
              selectedOptionId={skill.category}
              disabled={disabled || !skill.id}
            />
            <Icon
              rounded
              size="sm"
              icon="minus"
              variant="neutral"
              onClick={() => deleteWorkerSkill(index)}
              className="cursor-pointer"
            />
          </div>
        ))}

        <Button
          variant="transparent"
          onClick={handleAddWorkerSkill}
          disabled={disabled}
          className="w-fit ml-2"
        >
          + Add Worker Skill
        </Button>
      </PanelEntry>
    </div>
  );
};

export default GrantsConfigurator;
